import React, { useMemo } from 'react';
import {
  TrendingDown, CircleDot, Minus, Coins, RefreshCw,
  AlertCircle, BrainCircuit, Eye, ChevronDown
} from 'lucide-react';
import { LLMDecision } from '../../types/api';
import { Card, CardContent } from '../ui/card';
import { DecisionCard } from './DecisionCard';
import { cn } from '../../lib/utils';

interface PositionMonitoringCardProps {
  decision: LLMDecision;
  isLatest: boolean;
}

export const PositionMonitoringCard: React.FC<PositionMonitoringCardProps> = ({
  decision,
  isLatest,
}) => {
  const rec = decision.recommendation;

  // Memoize JSON stringification
  const jsonData = useMemo(() => {
    return JSON.stringify(
      { ...rec, _prompt_used: undefined },
      null,
      2
    );
  }, [rec]);

  return (
    <DecisionCard decision={decision} isLatest={isLatest}>
      <CardContent className="space-y-4">
        {/* Error Alert */}
        {rec?.error && (
          <Card className="bg-red-50/50 dark:bg-red-950/10 border-red-200 dark:border-red-800">
            <CardContent className="p-3">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-600 dark:text-red-400">
                    Consultation Error
                  </p>
                  <p className="text-xs text-red-500/80 mt-1">{rec.error}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {/* Action Card */}
          <Card className={cn(
            "bg-gradient-to-br",
            rec?.action === 'close_position'
              ? 'from-red-50 to-red-100/50 dark:from-red-950/20 dark:to-red-900/10'
              : rec?.action === 'hold'
              ? 'from-blue-50 to-blue-100/50 dark:from-blue-950/20 dark:to-blue-900/10'
              : 'from-gray-50 to-gray-100/50 dark:from-gray-950/20 dark:to-gray-900/10'
          )}>
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-1">
                {rec?.action === 'close_position' ? (
                  <TrendingDown className="h-3 w-3 text-red-600" />
                ) : rec?.action === 'hold' ? (
                  <CircleDot className="h-3 w-3 text-blue-600" />
                ) : (
                  <Minus className="h-3 w-3 text-gray-600" />
                )}
                <span className={cn(
                  "text-xs font-medium",
                  rec?.action === 'close_position' ? 'text-red-600' :
                  rec?.action === 'hold' ? 'text-blue-600' : 'text-gray-600'
                )}>Action</span>
              </div>
              <p className="text-sm font-bold capitalize">
                {rec?.action?.replace(/_/g, ' ') || 'No Action'}
              </p>
            </CardContent>
          </Card>

          {/* Position Symbol */}
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100/50 dark:from-purple-950/20 dark:to-purple-900/10">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-1">
                <Coins className="h-3 w-3 text-purple-600" />
                <span className="text-xs font-medium text-purple-600">Position</span>
              </div>
              <p className="text-sm font-bold">
                {decision.signal_data_summary?.symbol || rec?.symbol || 'N/A'}
              </p>
            </CardContent>
          </Card>

          {/* Consultation Count */}
          <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100/50 dark:from-indigo-950/20 dark:to-indigo-900/10">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-1">
                <RefreshCw className="h-3 w-3 text-indigo-600" />
                <span className="text-xs font-medium text-indigo-600">Consultations</span>
              </div>
              <p className="text-sm font-bold">
                {rec?.consultation_metadata?.consultation_count || 1}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Reasoning */}
        {rec?.reasoning && (
          <div>
            <h4 className="font-medium mb-2 flex items-center gap-2 text-sm">
              <BrainCircuit className="h-3 w-3" />
              Analysis
            </h4>
            <Card className="bg-muted/30">
              <CardContent className="p-3">
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {rec.reasoning}
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Full Data - Collapsible */}
        <details className="cursor-pointer group">
          <summary className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors">
            <Eye className="h-3 w-3" />
            <span>Show Full Decision Data</span>
            <ChevronDown className="h-3 w-3 transition-transform group-open:rotate-180" />
          </summary>
          <Card className="bg-muted/30 mt-2">
            <CardContent className="p-3">
              <div className="max-h-64 overflow-y-auto">
                <pre className="text-xs font-mono whitespace-pre-wrap break-words">
                  {jsonData}
                </pre>
              </div>
            </CardContent>
          </Card>
        </details>
      </CardContent>
    </DecisionCard>
  );
};

export default PositionMonitoringCard;
